import { useTranslations } from '@vocab/react';
import { Box, ContentBlock, Heading, Stack } from 'braid-design-system';
import React, { type FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import {
  createFlowPageIds,
  PAGE_ID_CLASSIFY,
  PAGE_ID_MANAGE,
  PAGE_ID_SELECT_AD_TYPE,
  PAGE_ID_WRITE,
} from 'src/app/shared/constants/pageUid';
import { actionUpdateProgress } from 'src/app/v1/state/modules/app/progress';
import { actionUpdateDraftProgress } from 'src/app/v1/state/modules/formData/draftProgress';
import type { AppState } from 'src/types/appState';
import type { DispatchDraftResponse } from 'src/types/draft';

import translations from './.vocab';
import { Step } from './components/Step';
import { isPreviousStep, isLaterStep } from './utils';

interface Props {
  handleDispatchDraft: () => DispatchDraftResponse;
  handlePreSubmit?: () => boolean;
  handleProgrammaticRouteChange: (stepId: string) => void;
  handleValidation?: () => string[];
}

const ProgressBar: FunctionComponent<Props> = ({
  handleDispatchDraft,
  handlePreSubmit,
  handleProgrammaticRouteChange,
  handleValidation,
}) => {
  const { t } = useTranslations(translations);
  const { pageId, progress } = useSelector(
    (state: AppState) => ({
      pageId: state.session.pageId,
      progress: state.app.progress,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const { currentPageId, furthestPageId, isValidObj } = progress;

  useEffect(() => {
    const newFurthestPageId = isLaterStep(pageId, furthestPageId)
      ? pageId
      : furthestPageId;

    const newProgress = {
      currentPageId: pageId,
      furthestPageId: newFurthestPageId,
      isValidObj: {
        // @NOTE: This spread below is important
        // otherwise later changes to newProgress can mutate
        // redux state variables (see https://redux.js.org/style-guide/#do-not-mutate-state)
        ...isValidObj,
      },
    };
    // In cases where navigation happens outside of this component
    // the validation state needs to be updated.
    // Any step previous to the current one is valid
    createFlowPageIds.forEach((routePageId) => {
      if (isPreviousStep(routePageId, currentPageId)) {
        newProgress.isValidObj[routePageId] = true;
      }
    });

    dispatch(actionUpdateProgress(newProgress));
    dispatch(actionUpdateDraftProgress(newProgress.furthestPageId));
  }, [currentPageId, dispatch, furthestPageId, isValidObj, pageId]);

  return (
    <ContentBlock>
      <Stack space="large">
        <Heading level="2">{t('progress-bar-heading')}</Heading>
        <Box display="flex" paddingX="large">
          <Step
            stepPageId={PAGE_ID_CLASSIFY}
            text={t('progress-bar-step-1-label')}
            handleDispatchDraft={handleDispatchDraft}
            handlePreSubmit={handlePreSubmit}
            handleProgrammaticRouteChange={handleProgrammaticRouteChange}
            handleValidation={handleValidation}
          />
          <Step
            stepPageId={PAGE_ID_SELECT_AD_TYPE}
            text={t('progress-bar-step-2-label')}
            handleDispatchDraft={handleDispatchDraft}
            handlePreSubmit={handlePreSubmit}
            handleProgrammaticRouteChange={handleProgrammaticRouteChange}
            handleValidation={handleValidation}
          />
          <Step
            stepPageId={PAGE_ID_WRITE}
            text={t('progress-bar-step-3-label')}
            handleDispatchDraft={handleDispatchDraft}
            handlePreSubmit={handlePreSubmit}
            handleProgrammaticRouteChange={handleProgrammaticRouteChange}
            handleValidation={handleValidation}
          />

          <Step
            stepPageId={PAGE_ID_MANAGE}
            text={t('progress-bar-step-4-label')}
            handleDispatchDraft={handleDispatchDraft}
            handlePreSubmit={handlePreSubmit}
            handleProgrammaticRouteChange={handleProgrammaticRouteChange}
            handleValidation={handleValidation}
          />
        </Box>
      </Stack>
    </ContentBlock>
  );
};

export default ProgressBar;
